import styles from "./Notes.module.css";
import { useState } from "react";
import { Button, CircleLoader } from "@wwg/ui";
import { useSessionStorage } from "react-use";
import { useTranslation } from "react-i18next";
import { ProspectInfoExpander } from "../../ProspectInfoExpander/ProspectInfoExpander";
import { InfoListItem } from "../../InfoListItem/InfoListItem";
import { useNotes } from "./useNotes";
import { Modal } from "shared/components/Modal/Modal";
import { ModalControls } from "shared/components/Modal/Modal";
import { ModalContent } from "shared/components/Modal/Modal";

export function Notes({ contact }) {
  const { t } = useTranslation();
  const [isAdding, setIsAdding] = useState(false);
  const { notes, addNoteMutation, updateNoteMutation, deleteNoteMutation } =
    useNotes(contact);

  // Takes the logID of the note we're asking to delete.
  const [confirmingDeleteFor, setConfirmingDeleteFor] = useState(null);

  const [isOpen, setIsOpen] = useSessionStorage("prospect-notes-open", false);

  return (
    <ProspectInfoExpander
      title={t("labels.prospectNotes")}
      onAdd={() => {
        setIsAdding(true);
      }}
      isOpen={isOpen}
      onToggle={setIsOpen}
    >
      {isAdding && (
        <form
          className={styles.editForm}
          onSubmit={(e) => {
            e.preventDefault();
            addNoteMutation.mutate({
              contactId: contact.id,
              note: e.target.note.value,
            });
            setIsAdding(false);
          }}
        >
          <textarea
            autoFocus={true}
            className={styles.editInput}
            name="note"
            placeholder={t("labels.prospectAddNewNote")}
          />
          <div className={styles.buttons}>
            <Button type="submit">{t("buttons.save")}</Button>
            <Button
              buttonStyle="hollow"
              onClick={(e) => {
                e.preventDefault();
                setIsAdding(false);
              }}
            >
              {t("buttons.cancel")}
            </Button>
          </div>
        </form>
      )}

      {notes.length === 0 && !isAdding && !addNoteMutation.isLoading && (
        <span className={styles.emptyLabel}>
          {t("labels.prospectNotesEmptyLabel")}
        </span>
      )}

      {addNoteMutation.isLoading && (
        <div className={styles.loadingPlaceholder}>
          <CircleLoader isLoading={true} />
        </div>
      )}

      {notes.length > 0 &&
        notes.map((note, i) => (
          <Note
            key={i}
            note={note}
            onSubmit={(value) => {
              updateNoteMutation.mutate({
                id: note.logId,
                contactId: contact.id,
                note: value,
              });
            }}
            onDelete={() => {
              setConfirmingDeleteFor(note.logId);
            }}
          />
        ))}

      <Modal isOpen={confirmingDeleteFor != null}>
        <ModalContent>
          <p>{t("labels.prospectNoteDeleteConfirm")}</p>
        </ModalContent>
        <ModalControls>
          <Button
            disabled={deleteNoteMutation.isLoading}
            buttonStyle="hollow"
            onClick={(e) => {
              e.preventDefault();
              setConfirmingDeleteFor(null);
            }}
          >
            {t("buttons.cancel")}
          </Button>
          <Button
            disabled={deleteNoteMutation.isLoading}
            buttonStyle="danger"
            onClick={(e) => {
              e.preventDefault();
              deleteNoteMutation.mutate(confirmingDeleteFor);
              setConfirmingDeleteFor(null);
            }}
          >
            {t("buttons.delete")}
          </Button>
        </ModalControls>
      </Modal>
    </ProspectInfoExpander>
  );
}

Notes.propTypes = {};

function Note({ note, onSubmit, onDelete }) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);

  if (isEditing) {
    return (
      <form
        className={styles.editForm}
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(e.target.note.value);
          setIsEditing(false);
        }}
      >
        <textarea autoFocus={true} className={styles.editInput} name="note">
          {note.details}
        </textarea>
        <div className={styles.buttons}>
          <Button type="submit">{t("buttons.save")}</Button>
          <Button
            buttonStyle="hollow"
            onClick={(e) => {
              e.preventDefault();
              setIsEditing(false);
            }}
          >
            {t("buttons.cancel")}
          </Button>
        </div>
      </form>
    );
  }

  return (
    <div className={styles.noteView}>
      <div className={styles.listItem}>
        <InfoListItem
          title={note.step}
          date={note.createdOn}
          content={note.details}
        />
      </div>

      <div className={styles.buttons}>
        <Button
          buttonStyle="hollow"
          onClick={(e) => {
            e.preventDefault();
            setIsEditing(true);
          }}
        >
          {t("buttons.edit")}
        </Button>
        <Button
          buttonStyle="danger"
          onClick={(e) => {
            e.preventDefault();
            setIsEditing(false);
            if (typeof onDelete === "function") {
              onDelete();
            }
          }}
        >
          {t("buttons.delete")}
        </Button>
      </div>
    </div>
  );
}
